import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';

import {
    AlternateAnchor,
    PrimaryButton,
    IconArrowRight,
    Colours,
    Form,
    PostalCodeInput,
    navigateWindowTo,
    Tab,
    Config,
    Locales,
    CondensedTheme,
    IconChooser,
    HiddenField,
    QuestionCompact,
    Sizes,
    messageToString,
    noticeError,
} from '@ratehub/base-ui';
import {
    INS_AUTO_BASE_ROUTES,
    INSURANCE_TYPES,
} from '@ratehub/ins-common';

import Paths from '../definitions/Paths';
import HomeInsuranceProviderRedirectsPropType from '../definitions/HomeInsuranceProviderRedirectsPropType';
import HOME_INSURANCE_QUOTE_LAUNCHER_MESSAGES from '../definitions/HomeInsuranceQuoteLauncherMessages';
import HOME_INSURANCE_TYPE_OPTIONS from '../definitions/HomeInsuranceQuoteLauncherOptions';
import useHomeInsuranceQuoteLauncher from '../hooks/useHomeInsuranceQuoteLauncher';
import getProvinceOptions from '../functions/getProvinceOptions';
import ButtonGroupContainer from './ButtonGroupContainer';


function HeroTabContentInsurance({ homeInsuranceProviderRedirects }) {
    const buttonGroupContainerRef = useRef(null);
    const [ shouldShowTabIcons, setShouldShowTabIcons ] = useState(true);

    // Icons in the button group should be hidden when the button group container is too narrow to save space.
    // We can determine when to hide icons by using a ResizeObserver to watch for changes in the button group container's width.
    useEffect(() => {
        if (!buttonGroupContainerRef.current) {
            noticeError(new Error('[HeroTabContentInsurance] Button group container ref is not assigned to an element'));
            return;
        }

        const element = buttonGroupContainerRef.current;

        const resizeObserver = new ResizeObserver(entries => {
            for (const entry of entries) {
                const { width } = entry.contentRect;

                const WIDTH_THAT_BREAKS_LAYOUT = 300;

                // If the button group container is too narrow, we should hide icons in the button group to save space.
                if (width < WIDTH_THAT_BREAKS_LAYOUT) {
                    setShouldShowTabIcons(false);
                } else {
                    setShouldShowTabIcons(true);
                }
            }
        });

        resizeObserver.observe(element);

        return () => {
            resizeObserver.disconnect();
        };
    });

    const buttonOptions = useMemo(() => ([
        {
            id: INSURANCE_TYPES.AUTO,
            title: (
                <FormattedMessage
                    id="web-components.HeroTabContentInsurance.buttonGroupContainer.auto"
                    defaultMessage="Auto"
                />
            ),
            iconKey: shouldShowTabIcons
                ? 'car'
                : undefined,
        },
        {
            id: INSURANCE_TYPES.HOME,
            title: (
                <FormattedMessage
                    id="web-components.HeroTabContentInsurance.buttonGroupContainer.home"
                    defaultMessage="Home"
                />
            ),
            iconKey: shouldShowTabIcons
                ? 'house'
                : undefined,
        },
        {
            id: INSURANCE_TYPES.LIFE,
            title: (
                <FormattedMessage
                    id="web-components.HeroTabContentInsurance.buttonGroupContainer.life"
                    defaultMessage="Life"
                />
            ),
            iconKey: shouldShowTabIcons
                ? 'heart'
                : undefined,
        },
    ]), [ shouldShowTabIcons ]);

    return (
        <ButtonGroupContainer
            ref={buttonGroupContainerRef}
            buttonGroupWidth="min(100%, 450px)"
            options={buttonOptions}
        >
            <Tab title="Auto insurance">
                <InsuranceContent
                    type={INSURANCE_TYPES.AUTO}
                />
            </Tab>
            <Tab title="Home insurance">
                <InsuranceContent
                    type={INSURANCE_TYPES.HOME}
                    providerRedirects={homeInsuranceProviderRedirects}
                />
            </Tab>
            <Tab title="Life insurance">
                <InsuranceContent
                    type={INSURANCE_TYPES.LIFE}
                />
            </Tab>
        </ButtonGroupContainer>
    );
}

HeroTabContentInsurance.propTypes = {
    homeInsuranceProviderRedirects: HomeInsuranceProviderRedirectsPropType.isRequired,
};

const InsuranceContent = observer(({ type, providerRedirects }) => {
    const intl = useIntl();
    const state = useHomeInsuranceQuoteLauncher(providerRedirects);

    const {
        img,
        sectionHeaderMessage,
        handleSubmit,
        formContents,
        ctaHref,
    } = INSURANCE_TYPE_CONTENTS[type];

    return (
        <Container className="rh-height-100p rh-display-grid rh-gap-1">
            {/* Column 1 */}
            <div className="image-wrapper rh-position-relative rh-width-100p rh-height-100p">
                <img
                    // Placeholder image until we have a proper design
                    src={img.src}
                    alt={messageToString(img.alt, intl)}
                    className={classNames(
                        'rh-position-absolute',
                        {
                            'circle': img.isCircle,
                            'rectangle rh-width-100p rh-height-100p': !img.isCircle,
                        },
                    )}
                />
            </div>

            {/* Column 2 */}
            <div className="rh-display-flex rh-flex-direction-column rh-justify-content-space-between rh-gap-1">
                <h2 className="rh-fg-blueberry-dark weight-medium rh-m-0 rh-text-align-center">
                    {sectionHeaderMessage}
                </h2>

                <Form
                    className="rh-flex-grow-1 rh-bg-stone-lightest rh-p-1 rh-display-flex rh-flex-direction-column rh-justify-content-center rh-gap-1"
                    onSubmit={providerRedirects
                        ? state.handleSubmit
                        : handleSubmit
                    }
                    onChange={providerRedirects
                        ? state.handleFieldChange
                        : null
                    }
                >

                    {formContents({
                        providerRedirects,
                        state,
                        locale: intl.locale,
                    })}

                    <PrimaryButton
                        message={(
                            <FormattedMessage
                                id="web-components.HeroTabContentInsurance.formSubmit"
                                defaultMessage="let's get started"
                            />
                        )}
                        type="submit"
                        size="small"
                        data-name={`${type}-insurance-form-submit`}
                        disabled={state.doNotService}
                    />
                </Form>

                <AlternateAnchor
                    href={ctaHref[intl.locale]}
                    size="small"
                    data-name={`${type}-insurance-learn-more`}
                >
                    <FormattedMessage
                        id="web-components.HeroTabContentInsurance.auto.alternateAnchor"
                        defaultMessage="learn more"
                    />
                    <IconArrowRight
                        stroke={Colours.BLUEBERRY_DARK}
                        strokeWidth="3px"
                        outlineWidth="0"
                        width="1.5rem"
                        height="1.5rem"
                        className="rh-ml-0_5"
                    />
                </AlternateAnchor>
            </div>
        </Container>
    );
});

InsuranceContent.propTypes = {
    type: PropTypes.oneOf([
        INSURANCE_TYPES.AUTO,
        INSURANCE_TYPES.HOME,
        INSURANCE_TYPES.LIFE,
    ]).isRequired,
    providerRedirects: HomeInsuranceProviderRedirectsPropType,
};

const INSURANCE_TYPE_CONTENTS = {
    [INSURANCE_TYPES.AUTO]: {
        img: {
            src: 'https://media.istockphoto.com/id/2213786669/photo/young-adult-couple-sit-on-car-trunk-and-use-mobile-phone-at-street.jpg?s=2048x2048&w=is&k=20&c=pH9Hl4NQYQcY0iaM2Ie5YhgcZQeF2XwegfmhDLwbkMg=',
            alt: (
                <FormattedMessage
                    id="web-components.HeroTabContentInsurance.auto.img.alt"
                    defaultMessage="Young adult couple sit on car trunk and use mobile phone at street"
                />
            ),
        },
        sectionHeaderMessage: (
            <FormattedMessage
                id="web-components.HeroTabContentInsurance.auto.title"
                defaultMessage="Compare car insurance quotes from Canada's top providers"
            />
        ),
        formContents: () => (
            <>
                <h3 className="rh-fg-blueberry-dark weight-medium rh-text-align-center rh-m-0">
                    <FormattedMessage
                        id="web-components.HeroTabContentInsurance.auto.formTitle"
                        defaultMessage="Get your free quotes today and save"
                    />
                </h3>
                <PostalCodeInput
                    name="postal"
                />
            </>
        ),
        handleSubmit: (values) => {
            const BASE_URL = Config.RATEHUB_BASE_URL;
            const SLUG = INS_AUTO_BASE_ROUTES.EN;
            const destinationUrl = new URL(BASE_URL + SLUG);

            destinationUrl.searchParams.set('postal', values.postal);
            navigateWindowTo(destinationUrl.toString());
        },
        ctaHref: {
            [Locales.ENGLISH]: Paths.INS_AUTO_LANDING_PAGE_EN,
            [Locales.FRENCH]: Paths.INS_AUTO_LANDING_PAGE_FR,
        },
    },
    [INSURANCE_TYPES.HOME]: {
        img: {
            src: 'https://media.istockphoto.com/id/1398121746/id/foto/konten-muda-bahagia-keluarga-bule-memegang-kardus-sebagai-atap-menutupi-mereka-duduk-di-lantai.jpg?s=2048x2048&w=is&k=20&c=P16HKVD3VX19EA8c8I35mePfUQ8zDRm7YJJSG03_M_s=',
            alt: (
                <FormattedMessage
                    id="web-components.HeroTabContentInsurance.home.img.alt"
                    defaultMessage="Happy young family sitting on the floor at home holding cardboard as a pretend roof covering them."
                />
            ),
        },
        sectionHeaderMessage: (
            <FormattedMessage
                id="web-components.HeroTabContentInsurance.home.title"
                defaultMessage="Save hundreds a year by comparing home insurance quotes"
            />
        ),
        formContents: ({ providerRedirects, state }) => (
            <CondensedTheme value={true}>
                <h3 className="rh-fg-blueberry-dark weight-medium rh-text-align-center rh-m-0">
                    <FormattedMessage
                        id="web-components.HeroTabContentInsurance.home.formTitle"
                        defaultMessage="Get a personalized quote"
                    />
                </h3>
                <QuestionCompact
                    field={{
                        name: 'insuranceTypeField', // Needs to match whats expected in useHomeInsuranceQuoteLauncher
                        options: HOME_INSURANCE_TYPE_OPTIONS,
                        type: 'choice',
                    }}
                    type="dropdown"
                    labelMessage={(
                        <FormattedMessage
                            id="web-components.HeroTabContentInsurance.home.input.insurance-type"
                            defaultMessage="Select insurance type"
                        />
                    )}
                    shouldRemoveMargin
                />
                <QuestionCompact
                    field={{
                        name: 'provinceField', // Needs to match whats expected in useHomeInsuranceQuoteLauncher
                        options: getProvinceOptions(providerRedirects),
                        type: 'choice',
                    }}
                    type="dropdown"
                    labelMessage={(
                        <FormattedMessage
                            id="web-components.HeroTabContentInsurance.home.input.province"
                            defaultMessage="Select province"
                        />
                    )}
                    canShowInvalid={state.doNotService}
                    onValidate={() => state.doNotService
                        ? HOME_INSURANCE_QUOTE_LAUNCHER_MESSAGES.INVALID_COMBINATION
                        : true}
                    shouldRemoveMargin
                />
            </CondensedTheme>
        ),
        ctaHref: {
            [Locales.ENGLISH]: Paths.INS_HOME_LANDING_PAGE_EN,
            [Locales.FRENCH]: Paths.INS_HOME_LANDING_PAGE_FR,
        },
    },
    [INSURANCE_TYPES.LIFE]: {
        img: {
            src: 'https://media.istockphoto.com/id/1049844230/photo/the-sunset-seems-closer-at-the-beach.jpg?s=2048x2048&w=is&k=20&c=9wFcnoJtPS8rgVSjO3TmVu4aLG1CLoJJFnzHi9xYPs0=',
            alt: (
                <FormattedMessage
                    id="web-components.HeroTabContentInsurance.life.img.alt"
                    defaultMessage="Rear-view shot of a happy young couple standing by the water’s edge and looking at the view of the ocean"
                />
            ),
            isCircle: true,
        },
        sectionHeaderMessage: (
            <FormattedMessage
                id="web-components.HeroTabContentInsurance.life.title"
                defaultMessage="Get a life insurance quote from Canada's top providers"
            />
        ),
        formContents: ({ locale }) => (
            <>
                <IconChooser
                    iconKey="headset"
                    size="2XL"
                    outlineWidth="0"
                    fillColour={Colours.BLUEBERRY_LIGHTEST}
                    stroke={Colours.BLUEBERRY_DARK}
                    className="rh-mb-1 rh-mx-auto"
                />
                <h3 className="rh-fg-blueberry-dark weight-medium rh-text-align-center rh-m-0">
                    <FormattedMessage
                        id="web-components.HeroTabContentInsurance.life.formTitle"
                        defaultMessage="Connect with one of our licensed brokers"
                    />
                </h3>
                <HiddenField
                    name="locale"
                    value={locale}
                />
            </>
        ),
        handleSubmit: (values) => {
            const href = {
                [Locales.ENGLISH]: Paths.INS_LIFE_QUOTER_EN,
                [Locales.FRENCH]: Paths.INS_LIFE_QUOTER_FR,
            };
            navigateWindowTo(href[values.locale]);
        },
        ctaHref: {
            [Locales.ENGLISH]: Paths.INS_LIFE_LANDING_PAGE_EN,
            [Locales.FRENCH]: Paths.INS_LIFE_LANDING_PAGE_FR,
        },
    },
};

const Container = styled.div`
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    grid-template-rows: auto;
    
    > .image-wrapper {
        min-height: 15rem;

        > img {
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            object-fit: cover;

            &.circle {
                max-width: 100%;
                max-height: 100%;
                border-radius: 100vh;
                aspect-ratio: 1/1;
            }

            &.rectangle {
                border-radius: 0 ${Sizes.SPACING.FOUR};
            }
        }
    }
`;

export default HeroTabContentInsurance;
